import React, { useEffect, useRef } from "react";
import { convertNumber } from "../../../../../utils/numberUtils.js";
import Highcharts from "highcharts";

const AreaChart = ({ data = [], radio_nsr }) => {
  const chartRef = useRef(null);

  const createCategories = (chartData) => {
    let categories = [];
    if (chartData && chartData.length > 0) {
      // Find the max valid day across all series
      let maxDay = 0;
      chartData.forEach((obj) => {
        (obj.data || []).forEach((item) => {
          if (item.sum !== null && item.day > maxDay) {
            maxDay = item.day;
          }
        });
      });
      const chartArr = chartData[0]?.data || [];
      if (chartArr.length > 0) {
        categories = chartArr
          .filter((item) => item.day <= maxDay)
          .map((item) => item.day);
      }
    }
    return categories;
  };

  const createSeries = (chartData, maxDay) => {
    let series = [];
    let seriesColor = ["#4085EC", "#D96123", "#7B3FA0", "#ADB458"];

    if (chartData && chartData.length > 0) {
      chartData.forEach((obj, idx) => {
        const seriesName = obj.series || "";
        const data = obj.data
          ? obj.data.filter((item) => item.day <= maxDay).map((item) => ({ y: item.y, ...item }))
          : [];

        // Area-only layer — only for the last series (selected month actuals)
        if (idx === chartData.length - 1) {
          series.push({
            name: seriesName + " (area)",
            data: data,
            type: "areaspline",
            lineWidth: 0,
            enableMouseTracking: false,
            showInLegend: false,
            zIndex: 1,
            color: seriesColor[idx % seriesColor.length],
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, "#E1ECFC"],
                [1, Highcharts.color("#E1ECFC").setOpacity(0).get("rgba")],
              ],
            },
          });
        }

        // Line-only layer
        series.push({
          name: seriesName,
          data: data,
          connectNulls: true,
          type: "spline",
          color: seriesColor[idx % seriesColor.length],
          lineColor: seriesColor[idx % seriesColor.length],
          lineWidth: 4,
          zIndex: 100 + idx,
          marker: {
            enabled: true,
            symbol: "circle",
            radius: 4,
            fillColor: "#fff",
            lineWidth: 2,
            lineColor: seriesColor[idx % seriesColor.length],
          },
          dataLabels: {
            enabled: false,
            formatter: function () {
              return parseInt(this.y).toLocaleString();
            },
          },
          isLineOnly: true, // custom flag
        });
      });
    }
    return series;
  };

  useEffect(() => {
    if (!chartRef.current) return;

    const categories = createCategories(data);
    const maxDay = categories.length > 0 ? categories[categories.length - 1] : 31;

    Highcharts.chart(chartRef.current, {
      credits: { enabled: false },
      exporting: { enabled: false },
      chart: {
        zoomType: "x",
        zooming: {
          mouseWheel: {
            enabled: true,
          },
        },
        panning: {
          enabled: true,
          type: "x",
        },

        panKey: "shift",
        style: { fontFamily: "Inter, sans-serif" },
        type: "areaspline",
        backgroundColor: "transparent",
        height: 400,
        spacing: [0, 0, 0, 5],
        padding: [0, 0, 0, 0],
        events: {
          load: function () {
            this.series.forEach((s) => {
              if (s.options.isLineOnly) {
                if (s.graph) s.graph.toFront();
                if (s.markerGroup) s.markerGroup.toFront();
                if (s.dataLabelsGroup) s.dataLabelsGroup.toFront();
              }
            });
          },
          redraw: function () {
            this.series.forEach((s) => {
              if (s.options.isLineOnly) {
                if (s.graph) s.graph.toFront();
                if (s.markerGroup) s.markerGroup.toFront();
                if (s.dataLabelsGroup) s.dataLabelsGroup.toFront();
              }
            });
          },
        },
      },
      title: { text: "MTD Chart" },
      xAxis: [
        {
          categories: categories,
          crosshair: true,
        },
      ],
      yAxis: [
        {
          visible: true,
          gridLineWidth: 1,
          title: { text: "Metric Ton" },
          labels: {
            formatter: function () {
              return convertNumber(this.value, 0);
            },
          },
        },
      ],
      tooltip: {
        shared: true,
        headerFormat: "",
        formatter: function () {
          const firstPoint = this.points && this.points[0];
          const xLabel = firstPoint
            ? (firstPoint.point.day ?? firstPoint.point.category ?? firstPoint.key ?? this.x)
            : (this.x ?? this.category ?? "");
          let s = "<b>" + xLabel + "</b>";
          this.points.forEach(function (point) {
            // skip area ghost series
            if (point.series.name.endsWith("(area)")) return;
            s +=
              "<br/><span style='color:" +
              point.series.color +
              "'>" +
              point.series.name +
              "</span>: <b>" +
              parseInt(point.y).toLocaleString() +
              "</span>: <b>" +
              " MT</b>"
              ;
          });
          return s;
        },
      },
      legend: {
        enabled: true,
        y: 0,
        floating: false
      },
      series: createSeries(data, maxDay),
    });
  }, [data]);

  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        height: "400px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    />
  );
};

export default AreaChart;
